import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createTicket, uploadAttachmentToBlob, fetchEmailTemplates, sendEmail, fetchSlaConfig, fetchRaciMatrix, fetchTickets } from './api';
import { useUser } from '../context/UserContext';
import CountryPicker from './CountryPicker';
import QuillEditor from './shared/QuillEditor';
import userService from '../services/userService';
import authService from '../services/authService';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

const TicketCreateModal = ({ isOpen, onClose, onTicketCreated, userRole, userEmail, countryCode, prefillData = null }) => {
  const { user } = useUser();
  const wasOpenRef = useRef(false);
  
  const [formData, setFormData] = useState({
    title: '',
    country_code: countryCode || '',
    country_iso3: '', // Add ISO3 field
    level_id: '', // Will be set after country selection
    store_name: '',
    thrive_id: '',
    lms_id: '',
    parent_store: '',
    antigen_name: [],
    priority: 'Medium',
    description: '',
    root_cause: 'Procurement',
    creator_email: user?.email || userEmail || '',
    assigned_to_email: '',
    oversight: '',
    org_unit_tags: [],
    custom_notify: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [storeOptions, setStoreOptions] = useState([]);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [slaConfig, setSlaConfig] = useState({
    low: { response: 144, resolution: 288 },
    medium: { response: 72, resolution: 144 },
    high: { response: 48, resolution: 72 },
    critical: { response: 24, resolution: 48 }
  });
  const [tagOptions, setTagOptions] = useState([]);
  const [mentionEmails, setMentionEmails] = useState([]);
  const [invalidAssignedEmails, setInvalidAssignedEmails] = useState([]);
  const [invalidCustomEmailIdxs, setInvalidCustomEmailIdxs] = useState([]);
  const toColor = (s) => {
    let hash = 0;
    for (let i = 0; i < String(s).length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    const palette = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0ea5e9', '#16a34a', '#9333ea', '#ea580c', '#6b7280'];
    const idx = Math.abs(hash) % palette.length;
    return palette[idx];
  };
  const tagSelectStyles = {
    multiValue: (base, state) => ({ ...base, backgroundColor: state.data.color, color: 'white' }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
    multiValueRemove: (base) => ({ ...base, color: 'white', ':hover': { backgroundColor: 'rgba(0,0,0,0.2)', color: 'white' } })
  };
  const parseEmails = (s) => String(s || '').split(/[;,\n\s]+/).map(x => x.trim()).filter(Boolean);
  const isValidEmailLocal = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
  
  // Attachment states
  const [attachments, setAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Quill editor is now handled by reusable component

  // Fetch stores and levels data for a country
  const fetchCountryData = useCallback(async (country, countryIso3Override = '') => {
    if (!country) return;
    
    try {
      setLoadingStores(true);
      // Use the ISO3 from formData if available, otherwise fallback to user context
      const iso3 = countryIso3Override || formData.country_iso3 ||
        (() => {
          const countryIndex = user?.allowedCountries?.indexOf(country);
          return countryIndex !== -1 && user?.allowedCountriesIso3?.[countryIndex]
            ? user.allowedCountriesIso3[countryIndex]
            : user?.country_iso3 || country;
        })();
      
      console.log(`Fetching country data for: ${country}, ISO3: ${iso3}`);
      
      const query = `
        SELECT DISTINCT 
            s.iso3, 
            g.Country_Name, 
            s.Store_Name, 
            s.Store_Name_Original,
            s.SC_Level_No, 
            s.lms_id,
            l.SC_Level_Name,
            s.Parent_Store, 
            s.province, 
            s.district
        FROM stores s 
        LEFT JOIN geo_admin g ON s.iso3 = g.iso3 
        LEFT JOIN sc_levels l ON s.SC_Level_No = l.SC_Level_No AND s.iso3 = l.iso3
        WHERE s.iso3 = '${iso3}' 
        ORDER BY s.Store_Name;
      `;
      
      const idToken = await authService.getIdToken();
      const response = await fetch('https://uni-func-pd-thrive360.azurewebsites.net/api/db-query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ query }),
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      // Handle the response structure - data might be wrapped in 'result' property
      const data = responseData.result || responseData;
      
      // Ensure data is an array before processing
      if (!Array.isArray(data)) {
        console.error('Expected array data but received:', data);
        setStoreOptions([]);
        setAvailableLevels([]);
        setCountryData([]);
        return;
      }
      
      // Extract unique levels for this country
      const levelMap = new Map();
      data.forEach(item => {
        if (item.SC_Level_No && !levelMap.has(item.SC_Level_No)) {
          levelMap.set(item.SC_Level_No, {
            levelNo: item.SC_Level_No,
            levelName: item.SC_Level_Name
          });
        }
      });
      const uniqueLevels = Array.from(levelMap.values()).sort((a, b) => a.levelNo - b.levelNo);
      
      // Store the country data for filtering
      setCountryData(data);
      setAvailableLevels(uniqueLevels);
      
      // Extract unique store names with full data
      const uniqueStores = [...new Set(data.map(item => item.Store_Name))].map(storeName => {
        const storeData = data.find(item => item.Store_Name === storeName);
        return {
          name: storeName,
          id: storeData?.lms_id || '',
          lmsId: storeData?.lms_id || '',
          parentStore: storeData?.Parent_Store || '',
          province: storeData?.province || '',
          district: storeData?.district || '',
          levelNo: storeData?.SC_Level_No || '',
          levelName: storeData?.SC_Level_Name || ''
        };
      });
      
      setStoreOptions(uniqueStores);

      // Auto-preselect for ERT when there is a single store (and optionally single level)
      if (String(iso3).toUpperCase() === 'ERT' && uniqueStores.length === 1) {
        const onlyStore = uniqueStores[0];
        const preLevel = onlyStore.levelNo || (uniqueLevels.length === 1 ? uniqueLevels[0].levelNo : '');
        setFormData(prev => ({
          ...prev,
          level_id: preLevel || '',
          store_name: onlyStore.name,
          thrive_id: onlyStore.id,
          lms_id: onlyStore.lmsId,
          parent_store: onlyStore.parentStore
        }));
        setStoreSearchTerm(onlyStore.name);
        // Narrow store list to the selected level if available
        if (preLevel) {
          const byLevel = uniqueStores.filter(s => String(s.levelNo) === String(preLevel));
          setStoreOptions(byLevel.length ? byLevel : uniqueStores);
        }
      }
    } catch (error) {
      console.error('Error fetching country data:', error);
      setStoreOptions([]);
      setAvailableLevels([]);
      setCountryData([]);
    } finally {
      setLoadingStores(false);
    }
  }, [formData.country_iso3, user?.allowedCountries, user?.allowedCountriesIso3, user?.country_iso3]);

  // Filter stores based on selected level
  const filterStoresByLevel = (levelId) => {
    if (!countryData.length) return;
    
    const filteredStores = countryData
      .filter(item => item.SC_Level_No === levelId)
      .map(item => ({
        name: item.Store_Name,
        id: item.lms_id || '',
        lmsId: item.lms_id || '',
        parentStore: item.Parent_Store || '',
        province: item.province || '',
        district: item.district || '',
        levelNo: item.SC_Level_No || '',
        levelName: item.SC_Level_Name || ''
      }));
    
    setStoreOptions(filteredStores);
  };

  // Handle country or level change
  const handleCountryOrLevelChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear store selection when country or level changes
    if (name === 'country_code' || name === 'level_id') {
      setFormData(prev => ({ 
        ...prev, 
        store_name: '', 
        thrive_id: '', 
        lms_id: '', 
        parent_store: '' 
      }));
      setStoreSearchTerm('');
      setStoreOptions([]);
    }
    
    // Fetch country data when country changes
    if (name === 'country_code') {
      fetchCountryData(value);
    } else if (name === 'level_id' && countryData.length > 0) {
      // Filter stores by selected level
      filterStoresByLevel(value);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'country_code' || name === 'level_id') {
      handleCountryOrLevelChange(name, value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Fetch SLA for selected country (by ISO3) so priority times are country-specific
  useEffect(() => {
    const loadSla = async () => {
      try {
        const iso3 = (formData.country_iso3 || '').toUpperCase();
        if (!iso3) return;
        const data = await fetchSlaConfig(iso3);
        if (data && data.low && data.medium && data.high && data.critical) {
          setSlaConfig(data);
        }
      } catch (e) {
        // keep defaults on error
      }
    };
    loadSla();
  }, [formData.country_iso3]);

  // Prefill Assigned To with Tier 1 emails for selected country (user can edit/append)
  useEffect(() => {
    const loadTier1 = async () => {
      try {
        const iso3 = (formData.country_iso3 || '').toUpperCase();
        if (!iso3) return;
        const raci = await fetchRaciMatrix(iso3);
        const tiers = raci?.tiers;
        if (!tiers || !tiers.level1) return;
        const tier1 = [
          ...(Array.isArray(tiers.level1.unicefCo) ? tiers.level1.unicefCo : []),
          ...(Array.isArray(tiers.level1.epi) ? tiers.level1.epi : [])
        ];
        const unique = Array.from(new Set(tier1.map(e => String(e || '').trim()).filter(Boolean)));
        if (unique.length) {
          setFormData(prev => ({
            ...prev,
            assigned_to_email: prev.assigned_to_email ? prev.assigned_to_email : unique.join(', ')
          }));
        }
      } catch (e) {
        // ignore
      }
    };
    loadTier1();
  }, [formData.country_iso3]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Resolve ISO3 directly from formData; the CountryPicker sets this
      const countryIso3 = String(formData.country_iso3 || '').toUpperCase();

      // If ERT with Custom priority, validate custom notification rows
      console.log('countryIso3', countryIso3);
      console.log('formData.priority', formData.priority);
      if (String(countryIso3).toUpperCase() === 'ERT' && String(formData.priority) === 'Custom') {
        const rows = Array.isArray(formData.custom_notify) ? formData.custom_notify : [];
        if (!rows.length) {
          setError('Please add at least one custom notification row for ERT Custom priority.');
          setIsSubmitting(false);
          return;
        }
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i] || {};
          const email = String(r.email || '').trim();
          const org = String(r.organization || '').trim();
          const daysVal = r.days;
          const isValidDays = typeof daysVal === 'number' ? daysVal >= 0 : String(daysVal).trim() !== '' && Number.isFinite(Number(daysVal)) && Number(daysVal) >= 0;
          if (!email || !org || !isValidDays) {
            setError(`Row ${i + 1}: email, organization, and days are required (days must be a non-negative number).`);
            setIsSubmitting(false);
            return;
          }
          if (!isValidEmailLocal(email)) {
            setError(`Row ${i + 1}: invalid email address.`);
            setIsSubmitting(false);
            return;
          }
        }
      }

      let attachmentMeta = {};
      if (attachments.length > 0) {
        const uploaded = await Promise.all(
          attachments.map(att => uploadAttachmentToBlob(att.file, 'data-dumps'))
        );
        attachmentMeta = {
          has_attachment: 1,
          attachment_name: attachments.map(a => a.name).join(', '),
          attachment_path: uploaded.map(u => u.path).join(', '),
          attachment_size: attachments.reduce((t, a) => t + a.size, 0),
          attachment_mime_type: attachments[0].type
        };
      }

      const ticketData = {
        ...formData,
        antigen_name: Array.isArray(formData.antigen_name)
          ? formData.antigen_name.join(', ')
          : formData.antigen_name,
        org_unit_tags: Array.isArray(formData.org_unit_tags) ? formData.org_unit_tags.join(', ') : '',
        ...attachmentMeta,
        country_code: countryIso3,
        creator_email: user?.email || userEmail || '',
        description: formData.description,
        created_at: new Date().toISOString()
      };

      // Only include custom_notify payload when applicable
      if (String(countryIso3).toUpperCase() === 'ERT' && String(formData.priority) === 'Custom') {
        ticketData.custom_notify = (formData.custom_notify || []).map(r => ({
          email: String(r.email || '').trim(),
          organization: String(r.organization || '').trim(),
          days: Number(r.days)
        }));
      } else {
        delete ticketData.custom_notify;
      }

      const assignedEmails = parseEmails(formData.assigned_to_email);
      // Front-end email validation
      if (!(String(countryIso3).toUpperCase() === 'ERT' && String(formData.priority) === 'Custom')) {
        const invalid = assignedEmails.filter(e => !isValidEmailLocal(e));
        if (invalid.length) {
          setInvalidAssignedEmails(invalid);
          setError('Please correct invalid email(s) in Assigned To.');
          setIsSubmitting(false);
          return;
        }
      }
      // Store first as the primary assigned user on the ticket
      ticketData.assigned_to_email = assignedEmails[0] || '';

      const newTicket = await createTicket(ticketData);
      onTicketCreated(newTicket);

      // Save tags to local storage for future suggestions
      try {
        const existing = JSON.parse(localStorage.getItem('org_unit_tags') || '[]');
        const set = new Set([...(Array.isArray(existing) ? existing : []), ...formData.org_unit_tags]);
        localStorage.setItem('org_unit_tags', JSON.stringify(Array.from(set)));
      } catch (_) {}

       // Send notification e-mails
      try {
        // 1. pull templates stored in Azure Blob
        const templates = await fetchEmailTemplates(); // { ticket_created, ticket_assigned, ... }

        const ticketUrl = `${window.location.origin}/ticket/${newTicket.id}`;
        const guestUrl = newTicket.guest_token
          ? `${window.location.origin}/guest/ticket/${newTicket.guest_token}`
          : null;

        // Helper to substitute common placeholders; works whether we get a raw string or an
        // object with an `html` field (new template format).
        const interpolate = (tpl = '', vars = {}) => {
          const html = typeof tpl === 'string' ? tpl : tpl?.html || '';
          const safe = (v) => (v == null ? '' : String(v));
          return html
            .replace(/{{\s*ticketId\s*}}/gi, safe(vars.ticketId))
            .replace(/{{\s*ticketUrl\s*}}/gi, safe(vars.ticketUrl))
            .replace(/{{\s*guestUrl\s*}}/gi, safe(vars.guestUrl))
            .replace(/{{\s*appUrl\s*}}/gi, safe(vars.appUrl))
            // Title variants (including common typo with stray quote)
            .replace(/{{\s*title\s*}}/gi, safe(vars.title))
            .replace(/{{\s*ticketTitle\s*}}/gi, safe(vars.title))
            .replace(/{{\s*ticketTittle"?\s*}}/gi, safe(vars.title))
            // Name  Store name variants
            .replace(/{{\s*storeName\s*}}/gi, safe(vars.storeName))
            .replace(/{{\s*name\s*}}/gi, safe(vars.name))
            // Priority and Status
            .replace(/{{\s*priority\s*}}/gi, safe(vars.priority))
            .replace(/{{\s*status\s*}}/gi, safe(vars.status))
            // Country
            .replace(/{{\s*country\s*}}/gi, safe(vars.country));
        };

        const emailPromises = [];

        const appUrl = ticketUrl;
        const baseVars = {
          ticketId: newTicket.id,
          ticketUrl,
          guestUrl,
          appUrl,
          title: newTicket.title || formData.title,
          country: newTicket.country_code || formData.country_iso3,
          priority: newTicket.priority || formData.priority,
          status: newTicket.status || 'Open',
          storeName: newTicket.store_name || formData.store_name || ''
        };

        // 1) Creator confirmation
        if (ticketData.creator_email) {
          emailPromises.push(
            sendEmail({
              to: ticketData.creator_email,
              from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
              subject: `THRIVE360: Ticket no. ${newTicket.id} was created`,
              html: interpolate(templates.ticket_created, { ...baseVars, name: ticketData.creator_email })
            })
          );
        }

        // 2) Notify assigned recipients (can be multiple)
        for (const assignedEmail of assignedEmails) {
          if (!assignedEmail || assignedEmail === ticketData.creator_email) continue;
          try {
            const userAccess = await userService.checkUserAccess(assignedEmail);
            if (userAccess.hasAccess) {
              emailPromises.push(
                sendEmail({
                  to: assignedEmail,
                  from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
                  subject: `Ticket ${newTicket.id} assigned to you`,
                  html: interpolate(templates.ticket_assigned, { ...baseVars, name: assignedEmail })
                })
              );
            } else if (guestUrl) {
              emailPromises.push(
                sendEmail({
                  to: assignedEmail,
                  from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
                  subject: `Ticket ${newTicket.id} – guest access`,
                  html: interpolate(templates.ticket_guest_access, { ...baseVars, name: assignedEmail })
                })
              );
            }
          } catch (userCheckError) {
            console.error('Failed to check user access for assigned recipient:', assignedEmail, userCheckError);
            if (guestUrl) {
              emailPromises.push(
                sendEmail({
                  to: assignedEmail,
                  from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
                  subject: `Ticket ${newTicket.id} – guest access`,
                  html: interpolate(templates.ticket_guest_access, { ...baseVars, name: assignedEmail })
                })
              );
            }
          }
        }

        // 3) Notify users mentioned in ticket description
        const extractMentionEmails = (html) => {
          const div = document.createElement('div');
          div.innerHTML = html || '';
          const mentionEls = div.querySelectorAll('.ql-mention, .mention');
          return Array.from(
            new Set(
              Array.from(mentionEls)
                .map((el) => el.dataset.id || el.dataset.value || el.innerText || '')
                .filter((e) => e && e.includes('@'))
            )
          );
        };

        const mentionEmails = extractMentionEmails(ticketData.description);
        mentionEmails.forEach((email) => {
          if (
            email &&
            email !== ticketData.creator_email &&
            email !== ticketData.assigned_to_email
          ) {
            emailPromises.push(
              sendEmail({
                to: email,
                from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
                subject: `You were mentioned in Ticket ${newTicket.id}`,
                html: interpolate(
                  templates.ticket_mentioned ||
                    `<p>You were mentioned in Ticket <a href="${ticketUrl}">#${newTicket.id}</a>.</p>`,
                  { ...baseVars, name: email }
                ),
              })
            );
          }
        });

        await Promise.all(emailPromises);
      } catch (mailErr) {
        // e-mail failures shouldn't block the UI flow
        console.error('Failed to send notification e-mails:', mailErr);
      }
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      setError(error.message || 'Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Attachment handling functions
  const handleFileSelect = (files) => {
    const newAttachments = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  // Options for antigen/vaccine selection (popular first)
  const antigenOptions = [
    'All',
    'ERT',
    'Penta IPV',
    'BCG',
    'bOPV',
    'COVID_19',
    'DTP_Penta',
    'HepB',
    'IPV',
    'MCV',
    'MOV',
    'PCV',
    'Percent_RI_S',
    'Rota',
    'TT_Td_DT',
    'YF',
    'Rabies',
    'Cholera',
    'HPV',
    'Typhoid',
    'MenA',
    'Malaria',
    'Hexa',
    'Other'
  ].map(label => ({ value: label, label }));


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.store-dropdown-container')) {
        setShowStoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update creator_email when user context changes
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        creator_email: user.email
      }));
    }
  }, [user?.email]);

  // Reset all fields each time the modal opens
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      const normalizedPrefill = prefillData ? {
        ...prefillData,
        country_iso3: String(prefillData.country_iso3 || '').toUpperCase(),
        level_id: prefillData.level_id ? Number(prefillData.level_id) : '',
        antigen_name: Array.isArray(prefillData.antigen_name)
          ? prefillData.antigen_name
          : (prefillData.antigen_name ? [prefillData.antigen_name] : []),
      } : null;

      setFormData({
        title: '',
        country_code: countryCode || '',
        country_iso3: '',
        level_id: '',
        store_name: '',
        thrive_id: '',
        lms_id: '',
        parent_store: '',
        antigen_name: [],
        priority: 'Medium',
        description: '',
        root_cause: 'Procurement',
        creator_email: user?.email || userEmail || '',
        assigned_to_email: '',
        oversight: '',
        org_unit_tags: [],
        custom_notify: [],
        ...(normalizedPrefill || {})
      });
      setAttachments([]);
      setIsDragOver(false);
      setStoreSearchTerm(normalizedPrefill?.store_name || '');
      setStoreOptions([]);
      setShowStoreDropdown(false);
      setLoadingStores(false);
      setCountryData([]);
      setAvailableLevels([]);
      setError(null);
      setInvalidAssignedEmails([]);
      setInvalidCustomEmailIdxs([]);

      if (normalizedPrefill?.country_code) {
        fetchCountryData(normalizedPrefill.country_code, normalizedPrefill.country_iso3);
      }
    }
  }, [isOpen, countryCode, user?.email, userEmail, prefillData, fetchCountryData]);

  // If a country name is set but ISO3 is empty, derive ISO3 from user context
  useEffect(() => {
    if (!formData.country_code || formData.country_iso3) return;
    try {
      const idx = user?.allowedCountries?.indexOf(formData.country_code);
      if (idx !== -1 && user?.allowedCountriesIso3?.[idx]) {
        setFormData(prev => ({ ...prev, country_iso3: String(user.allowedCountriesIso3[idx]).toUpperCase() }));
      }
    } catch (_) {}
  }, [formData.country_code, formData.country_iso3, user?.allowedCountries, user?.allowedCountriesIso3]);

  // Load tag suggestions when modal opens
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const tags = new Set();
        const emails = new Set();
        const local = JSON.parse(localStorage.getItem('org_unit_tags') || '[]');
        (Array.isArray(local) ? local : []).forEach(x => tags.add(String(x)));
        try {
          const tickets = await fetchTickets(userRole, null, { limit: 300 });
          (tickets || []).forEach(t => {
            const raw = t.org_unit_tags || '';
            if (!raw) return;
            String(raw)
              .split(/[;,]+/)
              .map(v => v.trim())
              .filter(Boolean)
              .forEach(v => tags.add(v));
            // collect emails for mentions
            if (t.creator_email) emails.add(String(t.creator_email).trim());
            if (t.assigned_to_email) emails.add(String(t.assigned_to_email).trim());
          });
        } catch (_) {}
        const options = Array.from(tags).sort().map(label => ({ value: label, label, color: toColor(label) }));
        setTagOptions(options);
        setMentionEmails(Array.from(emails));
      } catch (_) {}
    })();
  }, [isOpen, userRole]);



  const isErtCustom = String(formData.country_iso3 || '').toUpperCase() === 'ERT' && String(formData.priority || '') === 'Custom';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Create New Ticket</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full p-2 border rounded-md"
                placeholder="Brief description of the issue"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <CountryPicker
                value={formData.country_code ? {
                  value: formData.country_code,
                  label: formData.country_code,
                  iso3: formData.country_iso3
                } : null}
                onChange={async (opt) => {
                  const countryName = opt?.label || '';
                  const countryIso3 = opt?.iso3 || opt?.raw?.iso3 || (opt?.value ? String(opt.value).toUpperCase() : '');
                  setFormData(prev => ({
                    ...prev,
                    country_code: countryName,
                    country_iso3: countryIso3,
                    level_id: '',
                    store_name: '',
                    thrive_id: '',
                    lms_id: '',
                    parent_store: ''
                  }));
                  setStoreSearchTerm('');
                  setStoreOptions([]);
                  setAvailableLevels([]);
                  setShowStoreDropdown(false);
                  setCountryData([]);
                  // Fetch country data when country is selected
                  if (countryName) {
                    fetchCountryData(countryName);
                  }
                  // Assigned To prefill is handled by the useEffect on country_iso3
                }}
                placeholder="Select a country..."
                isDisabled={userRole === 'Country Office'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level <span className="text-red-500">*</span>
              </label>
              <select
                name="level_id"
                value={formData.level_id}
                onChange={(e) => {
                  const levelId = e.target.value ? parseInt(e.target.value) : '';
                  setFormData(prev => ({ ...prev, level_id: levelId }));
                  // Clear store selection when level changes
                  setFormData(prev => ({ ...prev, store_name: '', thrive_id: '', lms_id: '', parent_store: '' }));
                  setStoreSearchTerm('');
                  setStoreOptions([]);
                  setShowStoreDropdown(false);
                  // Filter stores by selected level
                  if (countryData.length > 0 && levelId) {
                    filterStoresByLevel(levelId);
                  }
                }}
                className="w-full p-2 border rounded-md"
                required
                disabled={!formData.country_code || loadingStores}
              >
                <option value="">Select country first</option>
                {availableLevels.map((level) => (
                  <option key={level.levelNo} value={level.levelNo}>
                    {level.levelName || `Level ${level.levelNo}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="relative store-dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={storeSearchTerm}
                onChange={(e) => {
                  setStoreSearchTerm(e.target.value);
                  setShowStoreDropdown(true);
                }}
                onFocus={() => setShowStoreDropdown(true)}
                className="w-full p-2 border rounded-md"
                placeholder={loadingStores ? "Loading stores..." : "Start typing store name..."}
                required
                disabled={!formData.country_code || !formData.level_id || loadingStores}
              />
              
              {/* Store dropdown */}
              {showStoreDropdown && storeOptions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {storeOptions
                    .filter(store => 
                      store.name.toLowerCase().includes(storeSearchTerm.toLowerCase())
                    )
                    .map((store, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                        onClick={() => {
                          setFormData(prev => ({ 
                            ...prev, 
                            store_name: store.name,
                            thrive_id: store.id,
                            lms_id: store.lmsId,
                            parent_store: store.parentStore
                          }));
                          setStoreSearchTerm(store.name);
                          setShowStoreDropdown(false);
                        }}
                      >
                        {store.name}
                      </div>
                    ))}
                </div>
              )}
              
              {/* No results message */}
              {showStoreDropdown && storeSearchTerm && storeOptions.length > 0 && 
               storeOptions.filter(store => 
                 store.name.toLowerCase().includes(storeSearchTerm.toLowerCase())
               ).length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-gray-500">
                  No stores found matching "{storeSearchTerm}"
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store ID (LMS ID)
              </label>
              <input
                type="text"
                value={formData.lms_id}
                className="w-full p-2 border rounded-md bg-gray-50"
                placeholder="Auto-populated from store selection"
                readOnly
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Store
              </label>
              <input
                type="text"
                value={formData.parent_store}
                className="w-full p-2 border rounded-md bg-gray-50"
                placeholder="Auto-populated from store selection"
                readOnly
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Antigen/Vaccine <span className="text-red-500">*</span>
              </label>
              <Select
                isMulti
                options={antigenOptions}
                value={antigenOptions.filter(opt => formData.antigen_name.includes(opt.value))}
                onChange={(selected) => {
                  setFormData(prev => ({
                    ...prev,
                    antigen_name: selected ? selected.map(s => s.value) : []
                  }));
                }}
                placeholder="Select antigens/vaccines..."
                classNamePrefix="react-select"
                className="react-select-container"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
                {String(formData.country_iso3 || '').toUpperCase() === 'ERT' && (
                  <option value="Custom">Custom</option>
                )}
              </select>
              {!(String(formData.country_iso3 || '').toUpperCase() === 'ERT' && String(formData.priority || '') === 'Custom') && (() => {
                const key = String(formData.priority || '').toLowerCase();
                const s = slaConfig[key] || {};
                const respH = Number(s.response) || 0;
                const resoH = Number(s.resolution) || 0;
                return (
                  <div className="mt-1 text-[11px] text-gray-500 leading-tight">
                    Response: {respH}h ({(respH/24).toFixed(1)}d), Resolution: {resoH}h ({(resoH/24).toFixed(1)}d)
                  </div>
                );
              })()}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Root Cause Category
              </label>
              <select
                name="root_cause"
                value={formData.root_cause}
                onChange={handleChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="Funding">Funding</option>
                <option value="Distribution">In Country Distribution</option>
                <option value="Procurement UNICEF">Procurement and Supply (UNICEF)</option>
                <option value="Storage">Storage capacity/functionality</option>
                <option value="Procurement EPI">Procurement and Supply (EPI)</option>
                <option value="Forecasting">Forecasting and allocation</option>
                <option value="Other">Other (explain in description)</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-0">
              Description <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500 whitespace-normal">
              Please provide a detailed description of the issue, including any relevant context.
              You can add images or files to the description.and mention other user with @Name.
            </span>
            <div className="border rounded-md overflow-hidden">
              <QuillEditor
                    value={formData.description}
                    onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                    placeholder="Detailed explanation of the issue..."
                    height="120px"
                    autoGrow
                    resizable
                    minHeight="120px"
                    maxHeight="500px"
                    mentionEmails={mentionEmails}
                    skipMentionFetch={true}
                  />
            </div>
          </div>
          
          {/* Attachment Section */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachments
            </label>
            
            {/* Drag & Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-2 text-center flex items-center justify-center transition-colors ${
                isDragOver 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center">
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop files here, or{' '}
                  <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
                    browse
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files)}
                    />
                  </label>
                </p>
               
              </div>
            </div>
            
            {/* Attachments List */}
            {attachments.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="text-sm text-gray-700">{attachment.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(attachment.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {!isErtCustom ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="assigned_to_email"
                value={formData.assigned_to_email}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData(prev => ({ ...prev, assigned_to_email: v }));
                  const emails = parseEmails(v);
                  const invalids = emails.filter(em => !isValidEmailLocal(em));
                  setInvalidAssignedEmails(invalids);
                }}
                className={`w-full p-2 border rounded-md ${invalidAssignedEmails.length ? 'border-red-500' : ''}`}
                placeholder="Comma-separated emails of responsible recipients"
                required
              />
              {invalidAssignedEmails.length > 0 && (
                <div className="mt-1 text-xs text-red-600">Invalid emails: {invalidAssignedEmails.join(', ')}</div>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Notifications (ERT only)
              </label>
              <div className="text-xs text-gray-500 mb-3">Define recipients to notify at specific day offsets from ticket creation. All fields are required.</div>
              <div className="space-y-2">
                {(formData.custom_notify || []).map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <input
                        type="email"
                        className={`w-full p-2 border rounded-md ${invalidCustomEmailIdxs.includes(idx) ? 'border-red-500' : ''}`}
                        placeholder="Email"
                        value={row.email || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData(prev => {
                            const arr = Array.isArray(prev.custom_notify) ? [...prev.custom_notify] : [];
                            arr[idx] = { ...(arr[idx] || {}), email: v };
                            return { ...prev, custom_notify: arr };
                          });
                          const valid = isValidEmailLocal(v);
                          setInvalidCustomEmailIdxs(prev => {
                            const set = new Set(prev);
                            if (!valid) set.add(idx); else set.delete(idx);
                            return Array.from(set);
                          });
                        }}
                        required
                      />
                      {invalidCustomEmailIdxs.includes(idx) && (
                        <div className="mt-1 text-xs text-red-600">Enter a valid email address</div>
                      )}
                    </div>
                    <div className="col-span-5">
                      <input
                        type="text"
                        className="w-full p-2 border rounded-md"
                        placeholder="Organization"
                        value={row.organization || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData(prev => {
                            const arr = Array.isArray(prev.custom_notify) ? [...prev.custom_notify] : [];
                            arr[idx] = { ...(arr[idx] || {}), organization: v };
                            return { ...prev, custom_notify: arr };
                          });
                        }}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full p-2 border rounded-md"
                        placeholder="Days"
                        value={row.days ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          const num = v === '' ? '' : Math.max(0, parseInt(v, 10) || 0);
                          setFormData(prev => {
                            const arr = Array.isArray(prev.custom_notify) ? [...prev.custom_notify] : [];
                            arr[idx] = { ...(arr[idx] || {}), days: num };
                            return { ...prev, custom_notify: arr };
                          });
                        }}
                        required
                      />
                    </div>
                    <div className="col-span-12 md:col-span-12 flex justify-end">
                      <button
                        type="button"
                        className="text-red-600 text-xs hover:underline"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            custom_notify: (prev.custom_notify || []).filter((_, i) => i !== idx)
                          }));
                          setInvalidCustomEmailIdxs(prev => prev.filter(i => i !== idx));
                        }}
                      >Remove</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  className="text-blue-600 text-xs hover:underline"
                  onClick={() => setFormData(prev => ({ ...prev, custom_notify: [...(prev.custom_notify || []), { email: '', organization: '', days: 0 }] }))}
                >+ Add recipient</button>
              </div>
            </div>
          )}

          {/* Org Unit Tags */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Org Unit Tags
            </label>
            <CreatableSelect
              isMulti
              options={tagOptions}
              value={(formData.org_unit_tags || []).map(v => ({ value: v, label: v, color: toColor(v) }))}
              onChange={(selected) => {
                const values = (selected || []).map(s => s.value);
                setFormData(prev => ({ ...prev, org_unit_tags: values }));
              }}
              onCreateOption={(inputValue) => {
                const v = inputValue.trim();
                if (!v) return;
                // Add to options and selection
                const option = { value: v, label: v, color: toColor(v) };
                setTagOptions(prev => {
                  const exists = prev.some(o => o.value.toLowerCase() === v.toLowerCase());
                  return exists ? prev : [...prev, option];
                });
                setFormData(prev => ({ ...prev, org_unit_tags: [...(prev.org_unit_tags || []), v] }));
              }}
              placeholder="Type to search or create tags..."
              classNamePrefix="react-select"
              className="react-select-container"
              styles={tagSelectStyles}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {(formData.org_unit_tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: toColor(tag), color: 'white' }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
    
          
          <div className="flex justify-end mt-6 gap-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketCreateModal; 